import moment from "moment";
import { getStreamDayRangeBySessions } from "../../../../common/src";
import {
    getCurrentDaySessions,
    getCurrentEpisodeSessions,
    getStreamLanguages,
    getStreamMeta,
    getStreamQualities,
    getStreamSessions
} from "../../../../common/src/stream";
import { RECORD_STATUSES } from "../../constants/common";
import { STORAGE_KEYS } from "./consts";

export const getStreamUrls = ({ stream, streamDay, quality, language, episode, recordStatus, embedEpisode}) => {
    const { isLive, isRecord, isWaiting } = getStreamMeta(stream);
    const momentNow = moment().unix();

    if (!stream || isWaiting || (!stream.dvr && !isLive) || embedEpisode && embedEpisode.timeStart > momentNow) {
        return null;
    }

    const { vodProfile, liveProfile } = stream;

    const viewServers = isRecord
        ? [vodProfile]
        : (liveProfile?.viewServer || [])
            .sort((a, b) => a.order - b.order)
            .map(({ server }) => server)
        ;

    return viewServers
        .map(({ viewUrl, type }) => {
            let query = [];
            let streamPath = '';
            let streamName = stream.name;
            let playlistName = 'index';

            if (type === 'flussonic') {
                if (episode && recordStatus === RECORD_STATUSES.EPISODES_CUT) {
                    streamName += '_' + episode.id;
                } else if (recordStatus === RECORD_STATUSES.RECORD_CUT) {
                    streamName += '_0';
                }

                if (isRecord) {
                    streamName += '.mp4';
                    streamPath += `update/${stream.recordUpdatedAt}/`;
                } else if (stream.dvr) {
                    const [streamDayStartTime, streamDayStopTime, streamDayDuration] = getStreamDayRangeBySessions(stream, streamDay, true);
                    const liveStop = 'now';
                    let start = embedEpisode?.timeStart || streamDayStartTime;
                    let stop = embedEpisode ? 
                        (embedEpisode.timeStop > momentNow ? liveStop : embedEpisode.timeStop - embedEpisode.timeStart) : 
                        (streamDayStopTime ? streamDayDuration : liveStop);

                    if (start) {
                        playlistName += '-' + start + '-' + stop;
                    }
                }
            } else {
                playlistName = 'playlist';

                if (stream.status === 'live' && stream.stream?.length > 0) {
                    streamName = stream.stream[streamDay ?? (isLive ? stream.stream.length - 1 : 0)].name;
                }

                streamName += '_' + quality.number;

                if (language) {
                    streamName += '_' + language.channel;
                }
                if (episode && recordStatus === RECORD_STATUSES.EPISODES_CUT) {
                    streamName += '_' + episode.id + '.mp4';
                } else if (recordStatus === RECORD_STATUSES.RECORD_CUT) {
                    streamName += '_0.mp4';
                } else if (recordStatus === RECORD_STATUSES.RECORD) {
                    streamName += '.mp4';
                }

                if (isRecord) {
                    streamPath += `update/${stream.recordUpdatedAt}/`;
                } else if (stream.dvr) {
                    query.push('DVR');
                }
            }

            return `${viewUrl}${streamPath}${streamName}/${playlistName}.m3u8?${query.join('&')}`;
        });
}

export const getQuality = (stream) => {
    const qualities = getStreamQualities(stream);
    const lastQualityNumber = parseInt(localStorage.getItem(STORAGE_KEYS.QUALITY)) || 0;
    return qualities.find(({ number }) => number === lastQualityNumber)
        || (qualities.length > 0 ? qualities[qualities.length - 1] : null);
}
export const getLanguage = (stream, localeName) => {
    const languages = getStreamLanguages(stream);
    if (languages.length < 2) {
        return null;
    }
    const lastLanguage = (localStorage.getItem(STORAGE_KEYS.LANGUAGE) || '')?.toLowerCase();
    return languages.find(({ label }) => [lastLanguage, localeName.toLowerCase()].includes(label.toLowerCase()))
        || languages[0]
        || null;
}

export const calculateEpisodeDuration = ({ episode, sessions }) => {
    if (!episode || !sessions?.length) return 0;

    const now = Math.round(Date.now() / 1000);
    let totalDuration = 0;

    sessions.forEach(({ timeStart, timeStop }) => {
        const start = Math.max(timeStart, episode.timeStart);
        const end = timeStop
            ? Math.min(timeStop, episode.timeStop)
            : Math.min(now, episode.timeStop);

        totalDuration += Math.max(0, end - start);
    });

    return Math.min(totalDuration, episode.timeStop - episode.timeStart);
};

export const getDurationSession = (stream, streamDay = null, episode = null) => {
    const sessions = getStreamSessions(stream);
    if (!sessions.length) return 0;

    if (episode && stream.episode?.length) {
        const episodeSessions = getCurrentEpisodeSessions({ stream, episode });
        return calculateEpisodeDuration({ episode, sessions: episodeSessions });
    }

    return getCurrentDaySessions(stream, streamDay).reduce((duration, { timeStart, timeStop }) => {
        return duration + Math.max(0, timeStop || Math.round(Date.now() / 1000) - timeStart);
    }, 0);
};